import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type React from 'react'
import type { RefObject } from 'react'
import isEmpty from 'lodash/isEmpty'
import toNumber from 'lodash/toNumber'
import { useClickAway, useDebounce, useToggle } from 'react-use'
import type { PivotCellTextRenderer } from '../constants/types'
import type { PivotCellRef, PivotLeafColumn, PivotResult, PivotRowItem, PivotSelectionValue } from '../pivot/types'
import type { PivotSummaryAnchor } from '../pivot/toPivotSummaryAnchor'
import { formatConstants, formatNumber, getWindow } from '@ztechapps/common.utils'
import { buildPivotSelectionTsv } from '../pivot/buildPivotSelectionTsv'
import { computePivotSelectionStats } from '../pivot/computePivotSelectionStats'
import { resolvePivotSummaryVisible } from '../pivot/resolvePivotSummaryVisible'
import { toPivotRowKey, toPivotRowLabelText } from '../pivot/pivotRow.utils'
import { toPivotSummaryAnchor } from '../pivot/toPivotSummaryAnchor'

/** Summary tooltip of a cell selection, tracked by one grid-level overlay. */
export type PivotSelectionSummary = PivotCellRef & { text: string; visible: boolean; anchor?: PivotSummaryAnchor }

/** Props for `usePivotCellSelection`. */
export type UsePivotCellSelectionProps = {
  result: PivotResult
  rows: PivotRowItem[]
  leafColumns: PivotLeafColumn[]
  headerRowCount: number
  renderCellText?: PivotCellTextRenderer
  rootRef: RefObject<HTMLDivElement | null>
  scrollRef: RefObject<HTMLDivElement | null>
  scrollTop: number
  scrollLeft: number
}

/** Selection state of the pivot grid body. */
export type UsePivotCellSelectionResult = {
  gridRef: RefObject<HTMLDivElement | null>
  gridProps: {
    tabIndex: number
    onMouseDown: (event: React.MouseEvent<HTMLDivElement>) => void
    onCopy: (event: React.ClipboardEvent<HTMLDivElement>) => void
  }
  isCellSelected: (rowIndex: number, column: number) => boolean
  summary?: PivotSelectionSummary
}

const EMPTY_KEYS = new Set<string>()

/** Every row index from one corner to the other × every column between them, as keys over `rows`. */
const rectangleKeys = (a: PivotCellRef, b: PivotCellRef, rows: PivotRowItem[]): Set<string> => {
  const keys = new Set<string>()
  for (let rowIndex = Math.min(a.rowIndex, b.rowIndex); rowIndex <= Math.max(a.rowIndex, b.rowIndex); rowIndex += 1) {
    const id = rows[rowIndex]?._id
    if (id === undefined) continue
    for (let column = Math.min(a.column, b.column); column <= Math.max(a.column, b.column); column += 1) {
      keys.add(`${column}:${id}`)
    }
  }
  return keys
}

/** The anchor cell's box for the grid-level summary overlay — one resolver for the anchor and visibility effects. */
const anchorCellBoxOf = (
  grid: HTMLDivElement | null,
  headerRowCount: number,
  rowIndex: number,
  column: number
): DOMRect | undefined => {
  const cell = grid?.querySelector(`tbody tr[aria-rowindex="${headerRowCount + rowIndex + 1}"]`)?.children[column]
  return cell instanceof HTMLElement ? cell.getBoundingClientRect() : undefined
}

/** Spreadsheet-style selection of pivot body cells — click, drag, Shift+click, Ctrl/Cmd+click — with an Average · Count · Sum tooltip and Ctrl/Cmd+C copy as tab-separated values. Copy stays on the native `copy` event, never a registered shortcut. */
export const usePivotCellSelection = ({
  result,
  rows,
  leafColumns,
  headerRowCount,
  renderCellText,
  rootRef,
  scrollRef,
  scrollTop,
  scrollLeft,
}: UsePivotCellSelectionProps): UsePivotCellSelectionResult => {
  const gridRef = useRef<HTMLDivElement>(null)
  const [state, setState] = useState<{
    rows: PivotRowItem[]
    leafColumns: PivotLeafColumn[]
    keys: Set<string>
    anchor?: PivotCellRef
  }>({ rows, leafColumns, keys: new Set() })
  const [summaryVisible, setSummaryVisible] = useToggle(false)

  // the selection counts only while the rows and columns it was built on are still current —
  // a new array (expand/collapse, Fields, sort, template) clears it with no effect, while a
  // scroll or a column-width change keeps the same arrays and the selection survives
  const current = state.rows === rows && state.leafColumns === leafColumns ? state : undefined
  const keys = current?.keys ?? EMPTY_KEYS

  // kept current on every render so the document listeners — attached once per drag, not
  // re-attached on every render — still read the latest grid
  const latestRef = useRef({ result, rows, leafColumns, headerRowCount })
  latestRef.current = { result, rows, leafColumns, headerRowCount }

  // a cell from an event target: a `td`/`th` inside a `tbody` of the grid whose `tr` carries `aria-rowindex`
  const cellFromTarget = useCallback((target: EventTarget | null): PivotCellRef | undefined => {
    const grid = gridRef.current
    if (!grid || !(target instanceof HTMLElement)) return undefined
    const cell = target.closest('td, th')
    if (!cell || !grid.contains(cell)) return undefined
    const row = cell.closest('tbody tr[aria-rowindex]')
    if (!row || !grid.contains(row)) return undefined
    const { result, rows, leafColumns, headerRowCount } = latestRef.current
    const rowIndex = toNumber(row.getAttribute('aria-rowindex')) - headerRowCount - 1
    const column = (cell as HTMLTableCellElement).cellIndex
    if (rowIndex < 0 || rowIndex >= rows.length) return undefined
    if (column < 0 || column >= result.rowFields.length + leafColumns.length) return undefined
    return { rowIndex, column }
  }, [])

  const readCellValue = useCallback(
    (rowIndex: number, column: number): PivotSelectionValue => {
      const row = rows[rowIndex]
      if (!row) return undefined
      if (column < result.rowFields.length) return toPivotRowLabelText(result, row, column, renderCellText)
      const leaf = leafColumns[column - result.rowFields.length]
      if (!leaf) return undefined
      return result.getValue(toPivotRowKey(result, row), leaf.columnKey, leaf.measureIndex)
    },
    [result, rows, leafColumns, renderCellText]
  )

  const rowIndexById = useMemo(() => new Map(rows.map((row, rowIndex) => [row._id, rowIndex])), [rows])
  const selectedCells = useMemo(() => {
    if (!current) return []
    const cells: PivotCellRef[] = []
    current.keys.forEach((key) => {
      const separator = key.indexOf(':')
      const rowIndex = rowIndexById.get(key.slice(separator + 1))
      if (rowIndex !== undefined) cells.push({ rowIndex, column: toNumber(key.slice(0, separator)) })
    })
    return cells
  }, [current, rowIndexById])

  const clearSelection = useCallback(() => {
    const { rows, leafColumns } = latestRef.current
    setState({ rows, leafColumns, keys: new Set() })
  }, [])
  useClickAway(rootRef, clearSelection)

  const dragRef = useRef<{ anchor: PivotCellRef; lastCell: string } | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  // Document listeners live only while dragging and come off in the effect's cleanup — the same
  // shape as `RouteOffCanvas`'s resize handle in `@ztechapps/webui.util`.
  useEffect(() => {
    if (!isDragging) return
    const doc = getWindow()?.document
    if (!doc) return

    const handleDragMove = (event: MouseEvent) => {
      const drag = dragRef.current
      if (!drag) return
      const cell = cellFromTarget(event.target)
      if (!cell) return
      if (`${cell.rowIndex}:${cell.column}` === drag.lastCell) return
      drag.lastCell = `${cell.rowIndex}:${cell.column}`
      const { rows, leafColumns } = latestRef.current
      setState({ rows, leafColumns, keys: rectangleKeys(drag.anchor, cell, rows), anchor: drag.anchor })
    }
    const handleDragUp = () => setIsDragging(false)

    doc.body.style.userSelect = 'none'
    doc.addEventListener('mousemove', handleDragMove)
    doc.addEventListener('mouseup', handleDragUp)
    return () => {
      dragRef.current = null
      doc.body.style.userSelect = ''
      doc.removeEventListener('mousemove', handleDragMove)
      doc.removeEventListener('mouseup', handleDragUp)
    }
  }, [isDragging, cellFromTarget])

  const onMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0) return
    const target = event.target as HTMLElement
    // links and controls inside a cell keep their own behaviour
    if (target.closest('a, button, input')) return
    const cell = cellFromTarget(target)
    if (!cell) return
    event.preventDefault()
    gridRef.current?.focus({ preventScroll: true })
    if (event.shiftKey) {
      const anchor = current?.anchor ?? cell
      setState({ rows, leafColumns, keys: rectangleKeys(anchor, cell, rows), anchor })
      return
    }
    if (event.ctrlKey || event.metaKey) {
      const next = new Set(keys)
      const key = `${cell.column}:${rows[cell.rowIndex]?._id}`
      if (next.has(key)) next.delete(key)
      else next.add(key)
      setState({ rows, leafColumns, keys: next, anchor: cell })
      return
    }
    // a plain press on a row label keeps the label's own click (expand/collapse) and leaves the
    // keys alone until the pointer reaches another cell
    const pressed =
      cell.column < result.rowFields.length ? new Set(keys) : new Set([`${cell.column}:${rows[cell.rowIndex]?._id}`])
    setState({ rows, leafColumns, keys: pressed, anchor: cell })
    dragRef.current = { anchor: cell, lastCell: `${cell.rowIndex}:${cell.column}` }
    setIsDragging(true)
  }

  const onCopy = (event: React.ClipboardEvent<HTMLDivElement>) => {
    if (isEmpty(selectedCells)) return
    let top = Infinity
    let bottom = -Infinity
    let left = Infinity
    let right = -Infinity
    selectedCells.forEach(({ rowIndex, column }) => {
      if (rowIndex < top) top = rowIndex
      if (rowIndex > bottom) bottom = rowIndex
      if (column < left) left = column
      if (column > right) right = column
    })
    const matrix: PivotSelectionValue[][] = []
    for (let rowIndex = top; rowIndex <= bottom; rowIndex += 1) {
      const line: PivotSelectionValue[] = []
      for (let column = left; column <= right; column += 1) {
        line.push(keys.has(`${column}:${rows[rowIndex]?._id}`) ? readCellValue(rowIndex, column) : undefined)
      }
      matrix.push(line)
    }
    event.clipboardData.setData('text/plain', buildPivotSelectionTsv(matrix))
    event.preventDefault()
  }

  const summaryTarget = useMemo(() => {
    if (selectedCells.length < 2) return undefined
    const stats = computePivotSelectionStats(
      selectedCells.map(({ rowIndex, column }) => readCellValue(rowIndex, column))
    )
    if (stats.count < 1) return undefined
    let bottom = -Infinity
    selectedCells.forEach(({ rowIndex }) => {
      if (rowIndex > bottom) bottom = rowIndex
    })
    let left = Infinity
    selectedCells.forEach(({ rowIndex, column }) => {
      if (rowIndex === bottom && column < left) left = column
    })
    return {
      rowIndex: bottom,
      column: left,
      text: `Average: ${formatNumber(stats.average, formatConstants.number2)} · Count: ${formatNumber(stats.count)} · Sum: ${formatNumber(stats.sum, formatConstants.number2)}`,
    }
  }, [selectedCells, readCellValue])

  // the tooltip shows only while its cell is rendered and its box lies inside the scroll area —
  // under the pinned header or Grand Total row, scrolled out, or outside the rendered window hides it.
  // The check runs on the settled scroll position, never per pixel, and while the pointer drives the
  // selection the last settled state holds — re-evaluating on each pixel would toggle it mid-drag.
  const [settledScroll, setSettledScroll] = useState({ top: scrollTop, left: scrollLeft })
  useDebounce(() => setSettledScroll({ top: scrollTop, left: scrollLeft }), 150, [scrollTop, scrollLeft])
  const [summaryAnchor, setSummaryAnchor] = useState<PivotSummaryAnchor>()
  const visibleRef = useRef(false)
  // the anchor mirrors the visibility hold — the last good box survives a query miss mid-drag
  const anchorRef = useRef<PivotSummaryAnchor | undefined>(undefined)

  // the grid-level overlay tracks the anchor cell's box per step — a style update on the same node,
  // never a remount — so one overlay follows the selection while it grows. While the pointer
  // drives the selection a query miss keeps the last good box — the dragged-over cells scroll
  // and re-render per pixel — instead of unmounting the overlay mid-drag.
  useLayoutEffect(() => {
    if (!summaryTarget) {
      anchorRef.current = undefined
      setSummaryAnchor(undefined)
      return
    }
    const grid = gridRef.current
    const gridBox = grid?.getBoundingClientRect()
    const cellBox = anchorCellBoxOf(grid, headerRowCount, summaryTarget.rowIndex, summaryTarget.column)
    if (gridBox && cellBox) {
      anchorRef.current = toPivotSummaryAnchor(cellBox, gridBox)
      setSummaryAnchor(anchorRef.current)
    } else if (isDragging) {
      setSummaryAnchor(anchorRef.current)
    } else {
      anchorRef.current = undefined
      setSummaryAnchor(undefined)
    }
  }, [summaryTarget, scrollTop, scrollLeft, rows, headerRowCount, isDragging])

  useLayoutEffect(() => {
    if (!summaryTarget) {
      visibleRef.current = false
      setSummaryVisible(false)
      return
    }
    let inView = false
    if (!isDragging) {
      const grid = gridRef.current
      const scroller = scrollRef.current
      const cellBox = anchorCellBoxOf(grid, headerRowCount, summaryTarget.rowIndex, summaryTarget.column)
      if (grid && scroller && cellBox) {
        const scrollBox = scroller.getBoundingClientRect()
        const headBottom = grid.querySelector('thead')?.getBoundingClientRect().bottom ?? scrollBox.top
        const footTop = grid.querySelector('tfoot')?.getBoundingClientRect().top ?? scrollBox.bottom
        inView =
          cellBox.top >= headBottom &&
          cellBox.bottom <= footTop &&
          cellBox.top >= scrollBox.top &&
          cellBox.bottom <= scrollBox.bottom &&
          cellBox.left >= scrollBox.left &&
          cellBox.right <= scrollBox.right
      }
    }
    const next = resolvePivotSummaryVisible({ isDragging, inView, wasVisible: visibleRef.current })
    visibleRef.current = next
    setSummaryVisible(next)
    // after drag end settles the cell out of view the overlay unmounts instead of tracking a hidden cell
    if (!isDragging && !inView) {
      anchorRef.current = undefined
      setSummaryAnchor(undefined)
    }
  }, [summaryTarget, settledScroll, rows, headerRowCount, isDragging, scrollRef, setSummaryVisible])

  const isCellSelected = (rowIndex: number, column: number): boolean => keys.has(`${column}:${rows[rowIndex]?._id}`)

  return {
    gridRef,
    gridProps: { tabIndex: -1, onMouseDown, onCopy },
    isCellSelected,
    summary: summaryTarget ? { ...summaryTarget, visible: summaryVisible, anchor: summaryAnchor } : undefined,
  }
}
